import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Shield,
  Key,
  Globe,
  Settings,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Lock,
  Unlock,
  Users,
  Clock,
  Monitor,
  ArrowRight,
  Copy,
  X,
  ChevronLeft,
  ChevronRight,
  Info,
  Zap,
  Search,
} from 'lucide-react';
import { notify } from '@core/notifications/notify';
import ConfirmationModal from '@/components/ConfirmationModal';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

interface SsoConfig {
  id: number;
  provider_type: 'saml' | 'oidc';
  entity_id?: string;
  sso_url?: string;
  slo_url?: string;
  certificate?: string;
  name_id_format?: string;
  issuer?: string;
  client_id?: string;
  client_secret?: string;
  auth_url?: string;
  token_url?: string;
  userinfo_url?: string;
  scopes?: string;
  is_active: boolean;
  enforce_sso: boolean;
  auto_provision: boolean;
  domain_whitelist: string[];
  attribute_mapping: Record<string, string>;
  default_role: string;
  created_at: string;
  updated_at: string;
}

interface LoginAttempt {
  id: number;
  email: string;
  status: string;
  ip_address: string;
  user_agent: string;
  error_message?: string;
  created_at: string;
}

type WizardTab = 'provider' | 'credentials' | 'domains' | 'mapping' | 'settings';

const WIZARD_TABS: { key: WizardTab; label: string }[] = [
  { key: 'provider', label: 'Provider' },
  { key: 'credentials', label: 'Credentials' },
  { key: 'domains', label: 'Domains' },
  { key: 'mapping', label: 'Mapping' },
  { key: 'settings', label: 'Settings' },
];

const DEFAULT_MAPPING: Record<string, string> = {
  email: 'email',
  first_name: 'given_name',
  last_name: 'family_name',
  display_name: 'name',
};

const SsoConfiguration: React.FC = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState<SsoConfig | null>(null);
  const [loginAttempts, setLoginAttempts] = useState<LoginAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Modal states
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activeWizardTab, setActiveWizardTab] = useState<WizardTab>('provider');

  // Wizard form
  const [form, setForm] = useState<Partial<SsoConfig>>({
    provider_type: 'oidc',
    is_active: false,
    enforce_sso: false,
    auto_provision: true,
    domain_whitelist: [],
    attribute_mapping: { ...DEFAULT_MAPPING },
    default_role: 'user',
  });
  const [domainInput, setDomainInput] = useState('');

  // Attempts pagination
  const [attemptsPage, setAttemptsPage] = useState(1);
  const attemptsPerPage = 10;

  useEffect(() => {
    loadConfig();
    loadLoginAttempts();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await fetch('/api/auth/sso/config', { headers: authHeaders() });
      if (!res.ok) {
        setConfig(null);
        return;
      }
      const data = await res.json();
      if (data && data.id) {
        setConfig({
          ...data,
          domain_whitelist: typeof data.domain_whitelist === 'string' ? JSON.parse(data.domain_whitelist) : data.domain_whitelist || [],
          attribute_mapping: typeof data.attribute_mapping === 'string' ? JSON.parse(data.attribute_mapping) : data.attribute_mapping || {},
        });
      } else {
        setConfig(null);
      }
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const loadLoginAttempts = async () => {
    setAttemptsLoading(true);
    try {
      const res = await fetch('/api/auth/sso/login-attempts', { headers: authHeaders() });
      if (!res.ok) {
        setLoginAttempts([]);
        return;
      }
      const data = await res.json();
      setLoginAttempts(Array.isArray(data) ? data : data?.attempts || []);
    } catch {
      setLoginAttempts([]);
    } finally {
      setAttemptsLoading(false);
    }
  };

  const openWizard = (existing?: SsoConfig) => {
    if (existing) {
      setForm({
        ...existing,
        domain_whitelist: [...(existing.domain_whitelist || [])],
        attribute_mapping: { ...DEFAULT_MAPPING, ...(existing.attribute_mapping || {}) },
      });
    } else {
      setForm({
        provider_type: 'oidc',
        is_active: false,
        enforce_sso: false,
        auto_provision: true,
        domain_whitelist: [],
        attribute_mapping: { ...DEFAULT_MAPPING },
        default_role: 'user',
      });
    }
    setActiveWizardTab('provider');
    setDomainInput('');
    setWizardOpen(true);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const method = config ? 'PUT' : 'POST';
      const body = {
        ...form,
        domain_whitelist: form.domain_whitelist || [],
        attribute_mapping: form.attribute_mapping || {},
      };
      const res = await fetch('/api/auth/sso/config', {
        method,
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (res.ok) {
        notify.success(`SSO configuration ${config ? 'updated' : 'created'} successfully`);
        setWizardOpen(false);
        loadConfig();
      } else {
        const err = await res.json().catch(() => ({}));
        notify.error(err.message || 'Failed to save configuration');
      }
    } catch {
      notify.error('Failed to save SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    if (!config) return;
    try {
      const res = await fetch(`/api/auth/sso/config/${config.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        notify.success('SSO configuration deleted');
        setConfig(null);
        setDeleteOpen(false);
      } else {
        notify.error('Failed to delete configuration');
      }
    } catch {
      notify.error('Failed to delete SSO configuration');
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const endpoint = config?.provider_type === 'oidc' ? '/api/auth/sso/oidc/initiate' : '/api/auth/sso/check-enforced';
      const res = await fetch(endpoint, { headers: authHeaders() });
      if (res.ok) {
        notify.success('SSO connection test successful');
      } else {
        notify.error('SSO connection test failed');
      }
    } catch {
      notify.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const addDomain = () => {
    const d = domainInput.trim().toLowerCase();
    if (d && !form.domain_whitelist?.includes(d)) {
      setForm({ ...form, domain_whitelist: [...(form.domain_whitelist || []), d] });
      setDomainInput('');
    }
  };

  const removeDomain = (domain: string) => {
    setForm({ ...form, domain_whitelist: (form.domain_whitelist || []).filter(d => d !== domain) });
  };

  const updateMapping = (key: string, value: string) => {
    setForm({ ...form, attribute_mapping: { ...(form.attribute_mapping || {}), [key]: value } });
  };

  const paginatedAttempts = loginAttempts.slice((attemptsPage - 1) * attemptsPerPage, attemptsPage * attemptsPerPage);
  const totalAttemptPages = Math.ceil(loginAttempts.length / attemptsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="text-blue-600" size={28} />
            SSO Configuration
          </h1>
          <p className="text-slate-500 mt-1">Manage Single Sign-On for your organization</p>
        </div>
        <div className="flex items-center gap-3">
          {config && (
            <>
              <button onClick={testConnection} disabled={testing} className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors disabled:opacity-50">
                <Zap size={16} />
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
              <button onClick={() => setDeleteOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors">
                <Trash2 size={16} />
                Delete
              </button>
            </>
          )}
          <button onClick={() => openWizard(config || undefined)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
            {config ? <Edit size={16} /> : <Plus size={16} />}
            {config ? 'Edit Config' : 'Setup SSO'}
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">SSO Status</p>
              <p className="text-lg font-semibold mt-1">{config?.is_active ? 'Active' : 'Inactive'}</p>
            </div>
            <div className={`p-3 rounded-xl ${config?.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
              {config?.is_active ? <CheckCircle className="text-green-600" size={24} /> : <XCircle className="text-slate-400" size={24} />}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Enforcement</p>
              <p className="text-lg font-semibold mt-1">{config?.enforce_sso ? 'Enforced' : 'Optional'}</p>
            </div>
            <div className={`p-3 rounded-xl ${config?.enforce_sso ? 'bg-amber-100' : 'bg-blue-100'}`}>
              {config?.enforce_sso ? <Lock className="text-amber-600" size={24} /> : <Unlock className="text-blue-600" size={24} />}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Provider</p>
              <p className="text-lg font-semibold mt-1 uppercase">{config?.provider_type || 'Not Configured'}</p>
            </div>
            <div className="p-3 rounded-xl bg-indigo-100">
              <Key className="text-indigo-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Current Config Display */}
      {config && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Settings size={20} className="text-slate-600" />
            Current Configuration
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Provider Type</p>
                <p className="text-sm font-medium text-slate-900 uppercase">{config.provider_type}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">{config.provider_type === 'oidc' ? 'Issuer' : 'Entity ID'}</p>
                <p className="text-sm font-medium text-slate-900 break-all">{config.provider_type === 'oidc' ? config.issuer : config.entity_id || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Auto Provision</p>
                <p className="text-sm font-medium text-slate-900">{config.auto_provision ? 'Enabled' : 'Disabled'}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Default Role</p>
                <p className="text-sm font-medium text-slate-900 capitalize">{config.default_role}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Whitelisted Domains</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {config.domain_whitelist?.length ? config.domain_whitelist.map(d => (
                    <span key={d} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">{d}</span>
                  )) : <span className="text-sm text-slate-400">None</span>}
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Last Updated</p>
                <p className="text-sm font-medium text-slate-900">{new Date(config.updated_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Cards */}
      {!config && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Globe className="text-blue-600" size={20} /></div>
              <div>
                <h3 className="font-semibold text-slate-900">SAML 2.0</h3>
                <p className="text-sm text-slate-600 mt-1">XML-based standard for exchanging authentication data. Works with ADFS, Okta, OneLogin, and most enterprise IdPs. Requires entity ID, SSO URL, and X.509 certificate.</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg"><Key className="text-purple-600" size={20} /></div>
              <div>
                <h3 className="font-semibold text-slate-900">OpenID Connect</h3>
                <p className="text-sm text-slate-600 mt-1">Modern OAuth 2.0-based protocol. Works with Azure AD, Google Workspace, Auth0, Keycloak. Requires client ID, client secret, and endpoint URLs.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Attempts */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Monitor size={20} className="text-slate-600" />
            Login Attempts
          </h2>
          <button onClick={loadLoginAttempts} disabled={attemptsLoading} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50">
            <RefreshCw size={14} className={attemptsLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
        {attemptsLoading ? (
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="animate-spin text-blue-600" size={24} />
          </div>
        ) : paginatedAttempts.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <Monitor size={40} className="mx-auto mb-2 opacity-50" />
            <p>No login attempts recorded</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">IP Address</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">User Agent</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedAttempts.map(a => (
                    <tr key={a.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-slate-900 font-medium">{a.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${a.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {a.status === 'success' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                          {a.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">{a.ip_address}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs max-w-[200px] truncate" title={a.user_agent}>{a.user_agent}</td>
                      <td className="px-4 py-3 text-red-600 text-xs max-w-[200px] truncate" title={a.error_message}>{a.error_message || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalAttemptPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <p className="text-sm text-slate-500">Page {attemptsPage} of {totalAttemptPages} ({loginAttempts.length} total)</p>
                <div className="flex gap-2">
                  <button onClick={() => setAttemptsPage(p => Math.max(1, p - 1))} disabled={attemptsPage === 1} className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50"><ChevronLeft size={16} /></button>
                  <button onClick={() => setAttemptsPage(p => Math.min(totalAttemptPages, p + 1))} disabled={attemptsPage === totalAttemptPages} className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50"><ChevronRight size={16} /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Wizard Modal */}
      {wizardOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
          <div className="relative min-h-full flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl ml-64">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Shield className="text-white" size={22} /></div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{config ? 'Edit SSO Configuration' : 'Setup SSO'}</h3>
                      <p className="text-white/70 text-sm">Configure your identity provider</p>
                    </div>
                  </div>
                  <button onClick={() => setWizardOpen(false)} className="p-2 bg-white/20 hover:bg-white/30 rounded-xl"><X className="text-white" size={20} /></button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-slate-200 px-6 bg-slate-50">
                {WIZARD_TABS.map(tab => (
                  <button key={tab.key} onClick={() => setActiveWizardTab(tab.key)} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeWizardTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4">
                {activeWizardTab === 'provider' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">Select your identity provider protocol:</p>
                    <div className="grid grid-cols-2 gap-4">
                      {(['saml', 'oidc'] as const).map(type => (
                        <button key={type} onClick={() => setForm({ ...form, provider_type: type })} className={`p-4 rounded-xl border-2 text-left transition-all ${form.provider_type === type ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            {type === 'saml' ? <Globe size={20} className="text-blue-600" /> : <Key size={20} className="text-purple-600" />}
                            <span className="font-semibold uppercase">{type}</span>
                          </div>
                          <p className="text-xs text-slate-500">{type === 'saml' ? 'XML-based, enterprise IdPs (ADFS, Okta)' : 'OAuth 2.0 based (Azure AD, Google, Auth0)'}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {activeWizardTab === 'credentials' && form.provider_type === 'saml' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Entity ID *</label>
                      <input type="text" value={form.entity_id || ''} onChange={e => setForm({ ...form, entity_id: e.target.value })} placeholder="https://idp.example.com/metadata" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">SSO URL *</label>
                      <input type="text" value={form.sso_url || ''} onChange={e => setForm({ ...form, sso_url: e.target.value })} placeholder="https://idp.example.com/sso" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">SLO URL</label>
                      <input type="text" value={form.slo_url || ''} onChange={e => setForm({ ...form, slo_url: e.target.value })} placeholder="https://idp.example.com/slo" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">X.509 Certificate *</label>
                      <textarea value={form.certificate || ''} onChange={e => setForm({ ...form, certificate: e.target.value })} placeholder="-----BEGIN CERTIFICATE-----" rows={4} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Name ID Format</label>
                      <select value={form.name_id_format || ''} onChange={e => setForm({ ...form, name_id_format: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
                        <option value="">Default</option>
                        <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress">Email Address</option>
                        <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified">Unspecified</option>
                        <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:persistent">Persistent</option>
                        <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:transient">Transient</option>
                      </select>
                    </div>
                  </div>
                )}

                {activeWizardTab === 'credentials' && form.provider_type === 'oidc' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Issuer *</label>
                      <input type="text" value={form.issuer || ''} onChange={e => setForm({ ...form, issuer: e.target.value })} placeholder="https://accounts.google.com" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Client ID *</label>
                      <input type="text" value={form.client_id || ''} onChange={e => setForm({ ...form, client_id: e.target.value })} placeholder="your-client-id" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Client Secret *</label>
                      <input type="password" value={form.client_secret || ''} onChange={e => setForm({ ...form, client_secret: e.target.value })} placeholder="your-client-secret" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Authorization URL *</label>
                      <input type="text" value={form.auth_url || ''} onChange={e => setForm({ ...form, auth_url: e.target.value })} placeholder="https://provider.com/authorize" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Token URL *</label>
                      <input type="text" value={form.token_url || ''} onChange={e => setForm({ ...form, token_url: e.target.value })} placeholder="https://provider.com/token" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">UserInfo URL</label>
                      <input type="text" value={form.userinfo_url || ''} onChange={e => setForm({ ...form, userinfo_url: e.target.value })} placeholder="https://provider.com/userinfo" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Scopes</label>
                      <input type="text" value={form.scopes || ''} onChange={e => setForm({ ...form, scopes: e.target.value })} placeholder="openid profile email" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                  </div>
                )}

                {activeWizardTab === 'domains' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">Restrict SSO login to specific email domains. Leave empty to allow all domains.</p>
                    <div className="flex gap-2">
                      <input type="text" value={domainInput} onChange={e => setDomainInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDomain())} placeholder="example.com" className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                      <button onClick={addDomain} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">Add</button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(form.domain_whitelist || []).map(d => (
                        <span key={d} className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded-full">
                          @{d}
                          <button onClick={() => removeDomain(d)} className="hover:text-red-600"><X size={14} /></button>
                        </span>
                      ))}
                    </div>
                    {(form.domain_whitelist || []).length === 0 && (
                      <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertTriangle size={16} className="text-amber-600" />
                        <p className="text-xs text-amber-700">No domains configured. All email domains will be accepted.</p>
                      </div>
                    )}
                  </div>
                )}

                {activeWizardTab === 'mapping' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">Map your identity provider attributes to user profile fields.</p>
                    {Object.entries(form.attribute_mapping || DEFAULT_MAPPING).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-3">
                        <div className="w-1/3">
                          <label className="text-sm font-medium text-slate-700 capitalize">{key.replace(/_/g, ' ')}</label>
                        </div>
                        <ArrowRight size={16} className="text-slate-400" />
                        <input type="text" value={val} onChange={e => updateMapping(key, e.target.value)} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
                      </div>
                    ))}
                    <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <Info size={16} className="text-blue-600" />
                      <p className="text-xs text-blue-700">These map IdP claim names to EIS-Tech user fields. Adjust based on your provider's claim names.</p>
                    </div>
                  </div>
                )}

                {activeWizardTab === 'settings' && (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-slate-900">Enforce SSO</p>
                        <p className="text-xs text-slate-500 mt-0.5">Require all users to log in via SSO. Password login will be disabled.</p>
                      </div>
                      <button onClick={() => setForm({ ...form, enforce_sso: !form.enforce_sso })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.enforce_sso ? 'bg-blue-600' : 'bg-slate-300'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.enforce_sso ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-slate-900">Auto Provision Users</p>
                        <p className="text-xs text-slate-500 mt-0.5">Automatically create accounts for new SSO users on first login.</p>
                      </div>
                      <button onClick={() => setForm({ ...form, auto_provision: !form.auto_provision })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.auto_provision ? 'bg-blue-600' : 'bg-slate-300'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.auto_provision ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <p className="text-sm font-medium text-slate-900">Active</p>
                        <p className="text-xs text-slate-500 mt-0.5">Enable or disable SSO login for your organization.</p>
                      </div>
                      <button onClick={() => setForm({ ...form, is_active: !form.is_active })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_active ? 'bg-green-600' : 'bg-slate-300'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Default Role</label>
                      <select value={form.default_role || 'user'} onChange={e => setForm({ ...form, default_role: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
                        <option value="user">User</option>
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex items-center justify-between">
                <div className="flex gap-2">
                  {activeWizardTab !== 'provider' && (
                    <button onClick={() => { const idx = WIZARD_TABS.findIndex(t => t.key === activeWizardTab); const prev = WIZARD_TABS[idx - 1]; if (idx > 0 && prev) setActiveWizardTab(prev.key); }} className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-200 rounded-lg hover:bg-slate-300">
                      Back
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setWizardOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-200 rounded-lg hover:bg-slate-300">Cancel</button>
                  {activeWizardTab !== 'settings' ? (
                    <button onClick={() => { const idx = WIZARD_TABS.findIndex(t => t.key === activeWizardTab); const next = WIZARD_TABS[idx + 1]; if (idx < WIZARD_TABS.length - 1 && next) setActiveWizardTab(next.key); }} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-1">
                      Next <ArrowRight size={14} />
                    </button>
                  ) : (
                    <button onClick={saveConfig} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                      {saving ? <><RefreshCw size={14} className="animate-spin" /> Saving...</> : <><CheckCircle size={14} /> Save Configuration</>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={deleteConfig}
        title="Delete SSO Configuration"
        message="Are you sure you want to delete this SSO configuration? All users will need to use password login. This action cannot be undone."
        confirmText="Delete Configuration"
        type="danger"
      />
    </div>
  );
};

export default SsoConfiguration;
